import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { Events } from 'src/providers/events/events';

import { DemoBlockModalComponent } from 'src/shared/components/demo-block-modal/demo-block-modal.component';
import { GetStartedFlowComponent } from 'src/shared/components/get-started-flow/get-started-flow.component';

/**
 * Gates actions that must not run in the shared sales-demo account (demos@tidy.com).
 *
 * Many prospects browse the same demo account at once, so any write (create / edit /
 * delete / book / send …) or any navigation that would land on a page without demo
 * data must be intercepted. When that happens we show a small "create an account"
 * prompt instead, whose "Get Started" button opens the same account-creation flow as
 * the demo banner.
 *
 * IMPORTANT: this only ever triggers inside the demo session. For every real account
 * {@link block} returns false and does nothing, so wiring it into a handler can never
 * change behavior for a real user — it can't brick the app.
 */
@Injectable({
  providedIn: 'root'
})
export class DemoBlockService {

  private demoAccountService = inject(DemoAccountService);
  private modalController = inject(ModalController);
  private rightSidePanelService = inject(RightSidePanelService);
  private windowService = inject(WindowService);
  private authModalService = inject(AuthModalService);
  private events = inject(Events);

  private isPromptOpen = false;

  /** Whether the current session is the shared demo account. */
  get isDemo(): boolean {
    return this.demoAccountService.isDemoSession();
  }

  /**
   * Gate a demo-restricted action. In the demo session this shows the "create an
   * account" prompt and returns true — the caller MUST abort its action:
   *
   *   handleClick() {
   *     if (this.demoBlock.block()) { return; }
   *     ...the real action...
   *   }
   *
   * Returns false (and does nothing) for every non-demo session, so the real action
   * proceeds untouched.
   */
  block(): boolean {
    if (!this.isDemo) {
      return false;
    }
    this.showPrompt();
    return true;
  }

  /**
   * Shows the shared prompt modal and runs `onConfirm` when its primary button is tapped.
   * Both the demo gate ({@link block}) and the new-rental booking gate
   * ({@link promptNewRental}) go through here so they share the single {@link isPromptOpen}
   * guard. Pass a `prompt` to override the modal's default demo copy.
   */
  private async showPrompt(
    prompt?: { title: string; body: string; buttonLabel: string },
    onConfirm?: () => void,
  ): Promise<void> {
    if (this.isPromptOpen) {
      return;
    }
    this.isPromptOpen = true;
    const modal = await this.modalController.create({
      component: DemoBlockModalComponent,
      componentProps: prompt ? { prompt } : undefined,
      cssClass: 'demo-block-modal-wrapper',
      backdropDismiss: true
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    this.isPromptOpen = false;
    if (data?.getStarted) {
      (onConfirm ?? (() => this.openGetStarted()))();
    }
  }

  /**
   * Opens the account-creation ("Get Started") flow — the same destination as the demo
   * banner's button. Desktop uses the right side panel that hosts the flow; mobile (where
   * the panel isn't rendered) falls back to the auth modal's sign-up.
   */
  openGetStarted(): void {
    // pm-schedule-call-first: route the prompt's CTA exactly like the demo indicator (Schedule
    // Call → schedule flow at step 2, or Create Account once they've already scheduled) via the
    // shared 'demo:get-started' → app.component.openGetStartedFlow path, so every gated action
    // sends the user to the right place. Control keeps the legacy account-creation flow below.
    if (this.demoAccountService.isScheduleCallFirstVariant()) {
      this.events.publish('demo:get-started');
      return;
    }
    if (this.windowService?.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Get Started');
      this.rightSidePanelService.openRightSidePanel(GetStartedFlowComponent);
    } else {
      this.authModalService.openAuthModal('auth', 'signup', '');
    }
  }

  /**
   * New-rental booking gate — the real-account counterpart to {@link block}. After a prospect
   * creates their own rental account but BEFORE they schedule their setup call, every attempt to
   * book a job (anywhere, via book.service.goToBookJob) is intercepted with the same "Schedule a
   * Call to Get Started" prompt the demo session shows, then sent to the Meet-Your-Account-Manager
   * page. Returns true only for a real, just-created rental account that hasn't scheduled yet.
   *
   * Cheap localStorage checks run first and short-circuit; the `isSetupCallScheduled` metadata
   * round-trip only happens for a freshly-created account, and self-heals the flag if the call
   * was already scheduled (e.g. booked outside the app). Existing rental users — who never get the
   * `justCreatedAccount` flag — are never gated.
   */
  async shouldGateNewRental(): Promise<boolean> {
    if (this.isDemo) { return false; }
    if (localStorage.getItem('justCreatedAccount') !== 'true') { return false; }
    if (localStorage.getItem('isRentalClient') !== 'true') { return false; }
    // Live server read (getMetadata) on every attempt — NOT a cached localStorage value — so a
    // concierge marking the call scheduled/completed in another system releases the gate on the
    // user's next booking click, with no client action. Either signal unblocks: the client-written
    // "scheduled" flag OR the concierge-written "completed" flag (Funnel V2 "Setup Call" checkbox).
    const [scheduled, completed] = await Promise.all([
      this.demoAccountService.isSetupCallScheduled(),
      this.demoAccountService.isSetupCallCompleted(),
    ]);
    if (scheduled || completed) {
      localStorage.removeItem('justCreatedAccount');
      return false;
    }
    return true;
  }

  /**
   * Shows the "Schedule a Call to Get Started" prompt for a new rental account; its CTA opens the
   * Meet-Your-Account-Manager page (get-started flow step 2). Shares {@link showPrompt}'s
   * single-open guard with the demo gate.
   */
  promptNewRental(): void {
    this.showPrompt(
      {
        title: 'Schedule a Call to Get Started',
        body: 'Schedule a quick call with your account manager to get set up.',
        buttonLabel: 'Schedule a Call',
      },
      () => this.openMyamForNewUser(),
    );
  }

  /**
   * Opens the get-started flow at the Meet-Your-Account-Manager page (step 2) for a REAL account,
   * mirroring app.component.openScheduleCall (desktop panel) / openMobileOnboardingPill (mobile
   * full-page route). `forceAccountManagerPage` makes the flow still render an MYAM design for
   * skip-to-schedule users (a random non-skip variant) instead of jumping to the Calendly step.
   */
  private openMyamForNewUser(): void {
    const params = { startStep: 2, forceAccountManagerPage: true };
    this.rightSidePanelService.setDialogPageTitle('Schedule Setup Call');
    if (this.windowService?.isDesktopRes) {
      this.rightSidePanelService.openRightSidePanel(GetStartedFlowComponent, params);
    } else {
      this.rightSidePanelService.navigateTo('get-started', params, GetStartedFlowComponent);
    }
  }
}
